<template>
  <div class="user-group-detail-page" role="main" aria-label="User Group Detail Page">
    <v-container fluid class="user-group-detail-container" aria-labelledby="page-title">
      <v-card class="main-content-card" elevation="0">
        <section class="page-header-section mb-6" aria-label="Page Header">
          <div class="page-header-content">
            <div class="page-title-wrapper">
              <h1 id="page-title" class="page-title" tabindex="0">
                User Group Detail
              </h1>
            </div>

            <div class="header-actions-group" role="toolbar" aria-label="User group detail actions">
              <v-btn v-if="!StatusSync" id="button-sync-usergroupdetail" class="action-btn-sync" variant="elevated"
                size="large" rounded="lg" prepend-icon="mdi-sync" @click="Sync">
                <span class="btn-label">{{ "Sync Users" }}</span>
                <v-tooltip activator="parent" location="top">
                  <div class="tooltip-content">
                    Manually refresh the user list and permissions by pulling the latest data from the connected
                    external authentication service.
                  </div>
                </v-tooltip>
              </v-btn>

              <v-btn v-else id="button-sync-loading-usergroupdetail" class="action-btn-sync" variant="elevated"
                size="large" rounded="lg" disabled>
                <v-icon start class="mdi-spin">mdi-sync</v-icon>
                <span class="btn-label">{{ "Sync Users" }}</span>
                <v-tooltip activator="parent" location="top">
                  Syncing...
                </v-tooltip>
              </v-btn>

              <v-btn id="button-setup-usergroupdetail" class="action-btn-setup" variant="elevated" size="large"
                rounded="lg" router :to="'SetupUserGroup?id=' + groupID">
                <v-icon start size="20">mdi-cog</v-icon>
                <span class="btn-label">{{ "Setup Group" }}</span>
                <v-tooltip activator="parent" location="top">
                  <div class="tooltip-content">
                    Create a new organizational category to group and manage related configurations under a single title.
                  </div>
                </v-tooltip>
              </v-btn>
            </div>
          </div>
        </section>

        <section class="search-panel-section" aria-label="Search and Filter Group Users">
          <v-row class="align-center" no-gutters>
            <v-col cols="12" md="4" class="pr-md-3 mb-3 mb-md-0">
              <div class="custom-search-input-wrapper-full">
                <div class="custom-search-input-container">
                  <v-icon class="custom-search-input-icon">mdi-magnify</v-icon>
                  <input id="username-field-search-usergroupdetail" type="text" class="custom-search-input"
                    :placeholder="$t('username')" v-model="username" @keydown.enter="Search" />
                  <v-icon class="clear-icon" :style="username ? '' : 'color: transparent !important;'"
                    @click="username = ''">
                    mdi-close-circle
                  </v-icon>
                </div>
              </div>
            </v-col>

            <v-col cols="12" md="4" class="pr-md-3 mb-3 mb-md-0">
              <div class="custom-search-input-wrapper-full">
                <div class="custom-search-input-container">
                  <v-icon class="custom-search-input-icon">mdi-magnify</v-icon>
                  <input id="email-field-search-usergroupdetail" type="text" class="custom-search-input"
                    :placeholder="$t('email')" v-model="email" @keydown.enter="Search" />
                  <v-icon class="clear-icon" :style="email ? '' : 'color: transparent !important;'" @click="email = ''">
                    mdi-close-circle
                  </v-icon>
                </div>
              </div>
            </v-col>

            <v-col cols="12" md="4">
              <div class="w-100">
                <div class="d-flex align-center ga-1" style="justify-content: space-between;">
                  <div class="d-flex justify-center justify-md-start">
                    <v-btn id="button-search-usergroupdetail" class="search-btn-circular" icon size="40" @click="Search"
                      aria-label="Search group users">
                      <v-icon size="22" color="white">mdi-magnify</v-icon>
                      <v-tooltip activator="parent" location="top">
                        Search Users
                      </v-tooltip>
                    </v-btn>
                  </div>
                  <div class="vertical-divider mx-1"></div>
                  <div class="grow">
                    <span class="filter-label">{{ selectedRoleFilter === null ? 'All Roles' :
                      getRoleFilterLabel(selectedRoleFilter) }}</span>
                    <span class="text-gray-darken-1">({{ filteredRoleCount }})</span>
                  </div>
                </div>
              </div>
            </v-col>
          </v-row>
        </section>

        <v-data-table-server :items="filteredUsers" :items-per-page="itemsPerPage" :items-length="searchTotalTable"
          :loading="loadingTable" v-model:options="options" :headers="headers"
          class="rounded-xl user-group-data-table" hover fixed-header>
          <!-- No Data Template -->
          <template v-slot:no-data>
            <div class="no-data-container">
              <div class="no-data-content">
                <!-- Icon -->
                <v-icon size="64" color="grey-lighten-2" class="no-data-icon">
                  mdi-account-group-outline
                </v-icon>

                <!-- Message -->
                <div class="no-data-text">
                  <h3 class="no-data-title">Data not available</h3>
                  <p class="no-data-subtitle">No users found in this group</p>
                </div>

                <!-- Action Button -->
                <!-- <v-btn
                  color="primary"
                  variant="outlined"
                  size="default"
                  class="no-data-btn"
                  router
                  :to="'SetupUserGroup?id=' + groupID"
                >
                  <v-icon class="mr-2">mdi-plus</v-icon>
                  Add Users
                </v-btn> -->
              </div>
            </div>
          </template>

          <template v-slot:item="{ item }">
            <tr>
              <td style="text-align: left">{{ item.number }}</td>
              <td>{{ item.username }}</td>
              <td>{{ item.email }}</td>
              <td>{{ item.mobile }}</td>
              <td>{{ item.fullName }}</td>
              <td style="text-align: left">
                <div class="action-group" style="justify-content: left">
                  <!-- Edit -->
                  <v-btn id="edit-usergroupdetail" variant="text" density="comfortable" :disabled="StatusSync"
                    @click="EditUser(item)" class="action-btn edit-btn" icon>
                    <img :src="images.edit" alt="Edit" style="width: 25px; height: 25px;" />
                    <v-tooltip activator="parent" location="top">
                      Edit
                    </v-tooltip>
                  </v-btn>
                  <!-- Delete -->
                  <v-btn id="delete-usergroupdetail" variant="text" density="comfortable" :disabled="StatusSync"
                    @click="DeleteUser(item)" class="action-btn delete-btn" icon>
                    <img :src="images.delete" alt="Delete" style="width: 25px; height: 25px;" />
                    <v-tooltip activator="parent" location="top">
                      Delete
                    </v-tooltip>
                  </v-btn>
                </div>
              </td>
            </tr>
          </template>
          <template v-slot:bottom>
            <div class="table-footer-wrapper d-flex justify-space-between align-center px-4 py-2" style="width: 100%">
              <div>Total results {{ searchTotalTable }}</div>

              <v-data-table-footer :items-per-page-options="[10, 20, 50]" v-model:items-per-page="itemsPerPage"
                :page="options.page" :length="Math.ceil(searchTotalTable / (itemsPerPage > 0 ? itemsPerPage : 1))" />
            </div>
          </template>
        </v-data-table-server>
      </v-card>
    </v-container>

    <!-- EditUserDialog -->
    <v-dialog v-model="Editdialog" max-width="850px" persistent class="modern-dialog">
      <v-card class="dialog-card">
        <div class="dialog-header dialog-header-edit">
          <div class="header-main">
            <div class="header-text-content">
               <h2 class="dialog-title">Edit User Information</h2>
              <p class="dialog-subtitle">
                Edit user account information
              </p>
            </div>
          </div>
        </div>

        <v-divider></v-divider>

        <v-card-text class="edit-user-content">
          <div class="dialog-content-intro">
            <!-- Basic Information Section -->
            <div class="border-section-wrapper mb-7">
               <div class="basic-info-container">
                <img :src="images.basicInfoIcon" alt="Basic Info" class="basic-info-icon" />
                <span class="basic-info-text">Basic Information</span>
              </div>

              <v-row>
                <v-col cols="12" md="6">
                  <div class="input-wrapper">
                    <label class="input-label">
                      Username<span class="required-asterisk">*</span>
                    </label>
                    <v-text-field disabled :label="$t('username')" :rules="[rules.required]" v-model="edit.username"
                      variant="outlined" density="comfortable" class="modern-input">
                    </v-text-field>
                  </div>
                </v-col>
                <v-col cols="12" md="6">
                  <!-- Empty space for layout balance -->
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12" md="6">
                  <div class="input-wrapper">
                    <label class="input-label">First Name <span class="required-asterisk">*</span></label>
                    <v-text-field id="firstname-edit-usergroupdetail" :label="$t('firstname')" :rules="[rules.required]"
                      v-model="edit.firstname" variant="outlined" density="comfortable" class="modern-input">
                    </v-text-field>
                  </div>
                </v-col>
                <v-col cols="12" md="6">
                  <div class="input-wrapper">
                    <label class="input-label">Last Name <span class="required-asterisk">*</span></label>
                    <v-text-field id="lastname-edit-usergroupdetail" :label="$t('lastname')" :rules="[rules.required]"
                      v-model="edit.lastname" variant="outlined" density="comfortable" class="modern-input">
                    </v-text-field>
                  </div>
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12" md="6">
                  <div class="input-wrapper">
                    <label class="input-label">Email <span class="required-asterisk">*</span></label>
                    <v-text-field disabled :label="$t('email')" :rules="[rules.required, validateEmail(edit.email)]"
                      v-model="edit.email" variant="outlined" density="comfortable" class="modern-input">
                    </v-text-field>
                  </div>
                </v-col>
                <v-col cols="12" md="6">
                  <div class="input-wrapper">
                    <label class="input-label">Phone Number</label>
                    <v-text-field id="phonenumber-edit-usergroupdetail" :label="$t('phonenumber')" v-model="edit.phone"
                      v-mask="mask" variant="outlined" density="comfortable" class="modern-input">
                    </v-text-field>
                  </div>
                </v-col>
              </v-row>
            </div>

            <!-- Integration Social Section -->
            <div class="border-section-wrapper mb-7">
              <div class="basic-info-container">
                <img :src="images.socialIntegrationIcon" alt="Basic Info" class="basic-info-icon" />
                <span class="basic-info-text">Integration Social</span>
              </div>

              <v-row>
                <v-col cols="12" md="6">
                  <div class="input-wrapper">
                    <label class="input-label">
                      Social User ID<span class="required-asterisk">*</span>
                      <v-tooltip location="top start" max-width="300">
                        <template v-slot:activator="{ props }">
                          <v-icon v-bind="props" size="16" class="info-icon">
                            mdi-information-outline
                          </v-icon>
                        </template>
                        <div class="tooltip-content">
                          <div>{{ $t("SocialUserID") }}</div>
                        </div>
                      </v-tooltip>
                    </label>
                    <v-text-field id="lineuserid-edit-usergroupdetail" ref="socialUserID" :label="$t('SocialUserID')"
                      v-model="edit.socialUserID" variant="outlined" density="comfortable" class="modern-input">
                      <!-- <template v-slot:prepend-inner>
                        <LineIcon :size="18" :opacity="0.7" color="rgb(var(--v-theme-primary))" />
                      </template> 
                      <template v-slot:append-inner>
                        <v-icon class="help-icon" size="small" @click="
                          showSocialUserIDTooltip = !showSocialUserIDTooltip
                          ">
                          mdi-help-circle-outline
                        </v-icon>
                        <v-tooltip v-model="showSocialUserIDTooltip" activator="parent" location="top">
                          {{ $t("SocialUserID") }}
                        </v-tooltip>
                      </template> -->
                    </v-text-field>
                  </div>
                </v-col>
                <v-col cols="12" md="6">
                  <div class="input-wrapper">
                    <label class="input-label">
                      Recipient ID (Facebook)<span class="required-asterisk">*</span>
                      <v-tooltip location="top start" max-width="300">
                        <template v-slot:activator="{ props }">
                          <v-icon v-bind="props" size="16" class="info-icon">
                            mdi-information-outline
                          </v-icon>
                        </template>
                        <div class="tooltip-content">
                          <div>{{ $t("RecipientId") }}</div>
                        </div>
                      </v-tooltip>
                    </label>
                    <v-text-field id="facebookuserid-edit-usergroupdetail" ref="recipientID" :label="$t('RecipientId')"
                      v-model="edit.recipientID" variant="outlined" density="comfortable" class="modern-input">
                      <!-- <template v-slot:prepend-inner>
                        <v-icon size="18" color="primary">mdi-facebook</v-icon>
                      </template>
                      <template v-slot:append-inner>
                        <v-icon class="help-icon" size="small" @click="
                          showRecipientIDTooltip = !showRecipientIDTooltip
                          ">
                          mdi-help-circle-outline
                        </v-icon>
                        <v-tooltip v-model="showRecipientIDTooltip" activator="parent" location="top">
                          {{ $t("RecipientId") }}
                        </v-tooltip>
                      </template> -->
                    </v-text-field>
                  </div>
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12" md="6">
                  <div class="input-wrapper">
                    <label class="input-label">
                      Recipient ID (Instagram)<span class="required-asterisk">*</span>
                      <v-tooltip location="top start" max-width="300">
                        <template v-slot:activator="{ props }">
                          <v-icon v-bind="props" size="16" class="info-icon">
                            mdi-information-outline
                          </v-icon>
                        </template>
                        <div class="tooltip-content">
                          <div>{{ $t("RecipientId_Instagram") }}</div>
                        </div>
                      </v-tooltip>
                    </label>
                    <v-text-field id="instagramuserid-edit-usergroupdetail" ref="recipientId_Instagram"
                      :label="$t('RecipientId_Instagram')" v-model="edit.recipientId_Instagram" variant="outlined"
                      density="comfortable" class="modern-input">
                      <!-- <template v-slot:prepend-inner>
                        <v-icon size="18" color="primary">mdi-instagram</v-icon>
                      </template>
                      <template v-slot:append-inner>
                        <v-icon class="help-icon" size="small" @click="
                          showRecipientInstagramTooltip =
                          !showRecipientInstagramTooltip
                          ">
                          mdi-help-circle-outline
                        </v-icon>
                        <v-tooltip v-model="showRecipientInstagramTooltip" activator="parent" location="top">
                          {{ $t("RecipientId_Instagram") }}
                        </v-tooltip>
                      </template> -->
                    </v-text-field>
                  </div>
                </v-col>
              </v-row>
            </div>

            <!-- Model Section -->
            <div class="border-section-wrapper mb-2">
              <div class="basic-info-container">
                <img :src="images.modelConfigIcon" alt="Basic Info" class="basic-info-icon" />
                <span class="basic-info-text">{{ $t("ModelConfiguration") }}</span>
              </div>

              <v-row>
                <v-col cols="12" md="6">
                  <div class="input-wrapper">
                    <label class="input-label">
                      Model Grouping<span class="required-asterisk">*</span>
                      <v-tooltip location="top start" max-width="300">
                        <template v-slot:activator="{ props }">
                          <v-icon v-bind="props" size="16" class="info-icon">
                            mdi-information-outline
                          </v-icon>
                        </template>
                        <div class="tooltip-content">
                          <div>{{ $t("_InfoSelectGroupModel") }}</div>
                        </div>
                      </v-tooltip>
                    </label>
                    <v-autocomplete id="modelgrouping-edit-usergroupdetail" ref="groupModel" label="Model Grouping"
                      :search-input.sync="searchGroupModel" :items="listDDGroupModel" item-title="name" item-value="id"
                      clearable variant="outlined" density="comfortable" v-model="edit.groupModel" class="modern-input">
                      <!-- <template v-slot:append-inner>
                        <v-icon class="help-icon" size="small" @click="
                          showGroupModelTooltip = !showGroupModelTooltip
                          ">
                          mdi-help-circle-outline
                        </v-icon>
                        <v-tooltip v-model="showGroupModelTooltip" activator="parent" location="top">
                          {{ $t("_InfoSelectGroupModel") }}
                        </v-tooltip>
                      </template> -->
                    </v-autocomplete>
                  </div>
                </v-col>
                <v-col cols="12" md="6">
                  <div class="input-wrapper">
                    <label class="input-label">
                      Select Model<span class="required-asterisk">*</span>
                      <v-tooltip location="top start" max-width="300">
                        <template v-slot:activator="{ props }">
                          <v-icon v-bind="props" size="16" class="info-icon">
                            mdi-information-outline
                          </v-icon>
                        </template>
                        <div class="tooltip-content">
                          <div>{{ $t("_InfoSelectModel") }}</div>
                        </div>
                      </v-tooltip>
                    </label>
                    <v-autocomplete id="model-edit-usergroupdetail" ref="model" label="Model" :items="listDDModel"
                      item-title="name" item-value="modelDisplayID" multiple clearable variant="outlined"
                      density="comfortable" v-model="edit.model" class="modern-input model-autocomplete"
                      hide-details="auto" placeholder="Select Models"
                      :menu-props="{ closeOnContentClick: false, closeOnOutsideClick: false }">
                      <template #prepend-item>
                        <v-list-item class="model-option-item model-all-option"
                          :class="{ 'model-all-option--active': isAllModelsSelected(edit.model), 'model-all-option--disabled': !listDDModel || !listDDModel.length }"
                          density="compact" @mousedown.prevent @click.stop="toggleAllModels">
                          <template #prepend>
                            <v-checkbox-btn class="model-option-checkbox"
                              :model-value="isAllModelsSelected(edit.model)" color="#C30000" :ripple="false"
                              :disabled="!listDDModel || !listDDModel.length" @click.stop="toggleAllModels"></v-checkbox-btn>
                          </template>
                          <template #title>
                            <div class="model-all-title">
                              <span class="model-all-option__label">All Model</span>
                              <v-tooltip location="top">
                                <template #activator="{ props }">
                                  <v-icon v-bind="props" size="18" class="model-info-icon">
                                    mdi-information-outline
                                  </v-icon>
                                </template>
                                <span>All available models will be included.</span>
                              </v-tooltip>
                            </div>
                          </template>
                        </v-list-item>
                        <v-divider class="model-all-divider"></v-divider>
                      </template>

                      <template #item="{ props, item, index }">
                        <v-list-item v-bind="props" :id="'model-edit-usergroupdetail-option-' + index"
                          class="model-option-item"
                          :class="{ 'model-option-item--selected': isModelSelected(item.raw.modelDisplayID) }"
                          @click="props.onClick" @mousedown.prevent>
                          <template #prepend>
                            <v-checkbox-btn class="model-option-checkbox"
                              :model-value="isModelSelected(item.raw.modelDisplayID)" color="#C30000" :ripple="false"
                              @click.stop="props.onClick"></v-checkbox-btn>
                          </template>
                          <template #title>
                            <span class="model-option-title">{{ item.raw.name }}</span>
                          </template>
                        </v-list-item>
                      </template>

                      <template #selection="{ item, index }">
                        <span v-if="shouldCollapseModelChips(edit.model) && index === 0"
                          class="model-all-selected-label">
                          All Model
                          <v-tooltip location="top">
                            <template #activator="{ props }">
                              <v-icon v-bind="props" size="18" class="model-info-icon">
                                mdi-information-outline
                              </v-icon>
                            </template>
                            <span>All available models are selected.</span>
                          </v-tooltip>
                        </span>
                        <template v-else-if="!shouldCollapseModelChips(edit.model)">
                          <v-chip v-if="index < 3" class="model-chip" size="small" closable
                            @click:close="removeModel(item.value)">
                            <template #close>x</template>
                            {{ item.title }}
                          </v-chip>
                          <v-chip v-else-if="index === 3" class="model-chip more-chip" size="small">
                            +{{ edit.model.length - 3 }} more
                          </v-chip>
                        </template>
                      </template>
                    </v-autocomplete>
                  </div>
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12" md="6">
                  <div class="input-wrapper">
                    <label class="input-label">
                      Model Shortcut<span class="required-asterisk">*</span>
                      <v-tooltip location="top start" max-width="300">
                        <template v-slot:activator="{ props }">
                          <v-icon v-bind="props" size="16" class="info-icon">
                            mdi-information-outline
                          </v-icon>
                        </template>
                        <div class="tooltip-content">
                          <div>{{ $t("_InfoSelectQuickModel") }}</div>
                        </div>
                      </v-tooltip>
                    </label>
                    <v-autocomplete id="modelshortcut-edit-usergroupdetail" ref="modelQuickGroupID"
                      label="Model Shortcut" :items="DDQuickModel" item-title="name" item-value="id" clearable
                      variant="outlined" density="comfortable" v-model="edit.modelQuickGroupID" class="modern-input">
                      <!-- <template v-slot:append-inner>
                        <v-icon class="help-icon" size="small" @click="
                          showQuickModelTooltip = !showQuickModelTooltip
                          ">
                          mdi-help-circle-outline
                        </v-icon>
                        <v-tooltip v-model="showQuickModelTooltip" activator="parent" location="top">
                          {{ $t("_InfoSelectQuickModel") }}
                        </v-tooltip>
                      </template> -->
                    </v-autocomplete>
                  </div>
                </v-col>
              </v-row>
            </div>
          </div>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="dialog-actions">
          <div class="actions-content">
            <div class="actions-left">
              <v-btn id="button-changepassword-popupedit-usergroupdetail" variant="outlined" color="#C32E0E" @click="
                changePasswordDialog = true;
              edit.password = null;
              edit.confirmpassword = null;
              checkLengthPassword = true;
              checkLowercaseLetter = true;
              checkUppercaseLetter = true;
              checkNumber = true;
              checkSpecialCharacter = true;
              checkPasswordMatch = true;
              " class="dialog-action-btn px-4">
                <v-icon class="flip-right" start>mdi-lock-reset</v-icon>
                Change Password
              </v-btn>
            </div>
            <div class="actions-right">
              <v-btn variant="outlined" color="grey" @click="Editdialog = false" class="dialog-action-btn cancel-btn">
                <v-icon start>mdi-close</v-icon>
                {{ $t("close") }}
              </v-btn>
              <v-btn id="save-edit-usergroupdetail" variant="elevated" @click="UpdateUser"
                class="dialog-action-btn save-btn-edit">
                <v-img :src="images.newSaveImg" alt="Change Password Dialog" class="image-icon-style"></v-img>
                {{ $t("save") }}
              </v-btn>
            </div>
          </div>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Loading Please Wait -->
    <!-- <v-overlay v-model="dialog">
      <v-dialog v-model="dialog" persistent width="300">
        <v-card color="#1976D2" dark>
          <v-card-text class="white--text">
            Loading Please Wait...
            <v-progress-linear
              indeterminate
              color="#FAA91B"
              class="mb-0"
            ></v-progress-linear>
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-overlay> -->
    <v-overlay :model-value="dialog" class="align-center justify-center" persistent>
      <v-card class="loading-card" elevation="8">
        <v-card-text class="text-center">
          <v-progress-circular indeterminate size="64" color="primary" class="mb-4"></v-progress-circular>
          <h3 class="loading-title">Loading...</h3>
          <p class="loading-subtitle">Please wait</p>
        </v-card-text>
      </v-card>
    </v-overlay>

    <!--dialog change password-->
    <v-dialog v-model="changePasswordDialog" max-width="700px" persistent z-index="1000" class="modern-dialog">
      <v-card class="dialog-card">
        <div class="dialog-header">
          <div class="header-main">
            <div class="header-text-content">
              <h2 class="dialog-title">Change Password</h2>
              <p class="dialog-subtitle">
                Create a new secure password for your account
              </p>
            </div>
          </div>
        </div>

        <v-divider></v-divider>

        <v-card-text class="change-password-content">
          <!-- Security Tips -->
          <div class="security-tips-style template-description">
            <div class="alert-content">
              <strong>Security Tips:</strong>
              <ul class="tips-list">
                <li>Use a unique password that you don't use elsewhere</li>
                <li>Consider using a passphrase with mixed case, numbers, and symbols</li>
                <li>Avoid personal information like names, birthdays, or common words</li>
                <li>Change your password regularly for better security</li>
              </ul>
            </div>
          </div>

          <!-- Password Section -->
          <div class="form-section border-section-wrapper">
            <div class="section-title">
              <v-icon color="primary" class="mr-2">mdi-lock-outline</v-icon>
              Password Setup
            </div>

              <v-row class="form-row">
                <v-col cols="12" md="6">
                  <div class="input-wrapper">
                    <label class="input-label">New Password <span class="required-asterisk">*</span></label>
                    <v-text-field id="password-field-popupedit-usergroupdetail" ref="password"
                      v-model="edit.password" :rules="[rules.required]" :type="show1 ? 'text' : 'password'"
                      variant="outlined" density="compact" hide-details="auto" placeholder="New Password"
                      class="modern-input">
                      <template v-slot:append-inner>
                        <v-btn @click="show1 = !show1" icon variant="text" size="small" class="password-toggle">
                          <v-icon>{{ show1 ? 'mdi-eye' : 'mdi-eye-off' }}</v-icon>
                        </v-btn>
                      </template>
                    </v-text-field>

                    <!-- Password Requirements with Animation -->
                    <transition name="slide-fade" mode="out-in">
                      <div class="password-requirements" v-if="edit.password" key="requirements">
                        <div class="requirement-item" :class="{ valid: checkLengthPassword }">
                          <v-icon size="15" :color="checkLengthPassword ? '#2ABF56' : '#C30000'">
                            {{ checkLengthPassword ? "mdi-check" : "mdi-close" }}
                          </v-icon>
                          <span>{{ $t("checklengthcharacters") }}</span>
                        </div>
                        <div class="requirement-item" :class="{ valid: checkLowercaseLetter }">
                          <v-icon size="15" :color="checkLowercaseLetter ? '#2ABF56' : '#C30000'">
                            {{ checkLowercaseLetter ? "mdi-check" : "mdi-close" }}
                          </v-icon>
                          <span>{{ $t("checklowercaseletter") }}</span>
                        </div>
                        <div class="requirement-item" :class="{ valid: checkUppercaseLetter }">
                          <v-icon size="15" :color="checkUppercaseLetter ? '#2ABF56' : '#C30000'">
                            {{ checkUppercaseLetter ? "mdi-check" : "mdi-close" }}
                          </v-icon>
                          <span>{{ $t("checkuppercaseletter") }}</span>
                        </div>
                        <div class="requirement-item" :class="{ valid: checkNumber }">
                          <v-icon size="15" :color="checkNumber ? '#2ABF56' : '#C30000'">
                            {{ checkNumber ? "mdi-check" : "mdi-close" }}
                          </v-icon>
                          <span>{{ $t("checknumber") }}</span>
                        </div>
                        <div class="requirement-item" :class="{ valid: checkSpecialCharacter }">
                          <v-icon size="15" :color="checkSpecialCharacter ? '#2ABF56' : '#C30000'">
                            {{ checkSpecialCharacter ? "mdi-check" : "mdi-close" }}
                          </v-icon>
                          <span>{{ $t("checkspecialcharacter") }}</span>
                        </div>
                      </div>
                    </transition>
                  </div>
                </v-col>
                <v-col cols="12" md="6">
                  <div class="input-wrapper">
                    <label class="input-label">Confirm New Password <span class="required-asterisk">*</span></label>
                    <v-text-field id="confirmpassword-field-popupedit-usergroupdetail" ref="confirmpassword"
                      v-model="edit.confirmpassword" :rules="[rules.required]" :type="show2 ? 'text' : 'password'"
                      variant="outlined" density="compact" hide-details="auto" placeholder="Confirm New Password"
                      class="modern-input">
                      <template v-slot:append-inner>
                        <v-btn @click="show2 = !show2" icon variant="text" size="small" class="password-toggle">
                          <v-icon>{{ show2 ? 'mdi-eye' : 'mdi-eye-off' }}</v-icon>
                        </v-btn>
                      </template>
                    </v-text-field>

                    <!-- Password Match Indicator with Animation -->
                    <transition name="slide-fade" mode="out-in">
                      <div class="password-match" v-if="edit.confirmpassword" key="match">
                        <div class="requirement-item" :class="{ valid: checkPasswordMatch }">
                          <v-icon size="15" :color="checkPasswordMatch ? '#2ABF56' : '#C30000'">
                            {{ checkPasswordMatch ? "mdi-check" : "mdi-close" }}
                          </v-icon>
                          <span>{{ $t("checkpasswordmatch") }}</span>
                        </div>
                      </div>
                    </transition>
                  </div>
                </v-col>
              </v-row>
            </div>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="change-password-actions">
          <v-spacer></v-spacer>
          <v-btn variant="outlined" color="grey" @click="closeChangePassword" class="dialog-action-btn cancel-btn">
            {{ $t("cancel") }}
          </v-btn>
          <v-btn id="button-savechangepassword-popupedit-usergroupdetail" variant="elevated"
            @click="saveChangePassword" :disabled="!passwordIsValid" class="dialog-action-btn save-btn-change-password">
            <v-img :src="images.newSaveImg" alt="Update Password" class="image-icon-style"></v-img>
            {{ $t("save") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Apply Filter Confirmation Dialog -->
    <v-dialog v-model="applyFilterDialogOpen" max-width="520px" persistent class="apply-filter-confirm-dialog">
      <v-card class="apply-filter-confirm-card" rounded="sm">
        <v-card-title class="apply-filter-confirm-title px-5 py-3">
          Apply Filter
        </v-card-title>

        <v-card-text class="apply-filter-confirm-text px-5 py-6">
          Are you sure you want to apply the selected role filter?
        </v-card-text>

        <v-card-actions class="apply-filter-confirm-actions px-5 pb-3">
          <v-spacer></v-spacer>
          <v-btn variant="outlined" class="apply-filter-cancel-btn mr-3" @click="cancelApplyFilter">
            Cancel
          </v-btn>
          <v-btn class="apply-filter-apply-btn" @click="confirmApplyFilter">
            Apply
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import CryptoJS from "crypto-js";
import Cookies from "js-cookie";
import store from "../store";
import LogTrace from "@/api/function.js";
import { mask } from "vue-the-mask";
import LineIcon from "@/components/icons/LineIcon.vue";
import editImg from "@/assets/Icons/Edit.png";
import deleteImg from "@/assets/Icons/Delete.png";
import deleteAlertIcon from "@/assets/images/trash-solid.svg";
import basicInfoIcon from "@/assets/Icons/basic_info.svg";
import socialIntegrationIcon from "@/assets/Icons/network-chart.svg";
import modelConfigIcon from "@/assets/Icons/ai-duotone.svg";
import newSaveImg from "@/assets/Icons/white-save-floppy.svg";
// import DragDropFile from '@/components/DragDropFile.vue';

export default {
  name: "UserManagement",
  inject: ["flagNav", "FontColorTitle", "SubNavbarBackGround"],
  components: { LineIcon },
  mixins: [LogTrace],
  directives: {
    mask,
  },
  data: () => ({
    mask: "##########",
    show1: false,
    formHasErrors: false,
    permission: {
      add: true,
      edit: true,
      delete: true,
    },
    mailcheck: false,
    phonecheck: false,
    filters: {
      username: [],
      email: [],
      employeeName: [],
      userType: [],
    },
    roleFilterMenuOpen: false,
    selectedRoleFilter: null,
    tempSelectedRole: null,
    applyFilterDialogOpen: false,
    roleFilterOptions: [
      { label: "All Roles", value: null },
      { label: "Super Admin", value: 0 },
      { label: "Admin", value: 1 },
      { label: "User", value: 2 },
      { label: "Operator", value: 6 },
    ],
    DDRole: [
      {
        name: "Admin",
        value: 1,
      },
      {
        name: "ผู้ใช้งานทั่วไป",
        value: 2,
      },
    ],
    numberRules: [(v) => !!v || "Input is required!"],
    rules: {
      required: (value) => value === null || !!value || "Required.",
      min: (v) => v.length >= 10 || "Min 10 numbers",
    },
    Email: null,
    gg: null,
    UserType: null,
    dialog: false,
    edit: {
      id: null,
      username: null,
      firstname: null,
      lastname: null,
      email: null,
      phone: null,
      password: null,
      confirmpassword: null,
      roleId: null,
      userType: null,
      model: [],
      // recipientId: null,
    },
    user: {
      id: null,
      username: null,
      firstname: null,
      lastname: null,
      email: null,
      phone: null,
      password: null,
      roleId: 2,
      userType: null,
      model: [],
    },
    listDDModel: [],

    oldPassword: "",
    Editdialog: false,
    isSearch: false,
    username: "",
    email: "",
    totalTable: 0,
    itemsPerPage: 10,
    options: {},
    today: null,
    userList: [],
    headers: [],
    secretKey: "01234567890123456789012345678901",
    roleId: Cookies.get("roleId"),
    isFirst: true,
    searchTotalTable: 0,
    sortBy: [],
    sortDesc: [],
    fileImportUser: null,
    flagTemplate: false,
    groupID: null,
    listDDGroupModel: null,
    DDQuickModel: null,
    searchGroupModel: null,
    StatusSync: false,
    loadingTable: false,
    isInitialized: false,

    changePasswordDialog: false,
    checkLengthPassword: true,
    checkLowercaseLetter: true,
    checkUppercaseLetter: true,
    checkNumber: true,
    checkSpecialCharacter: true,
    checkPasswordMatch: true,
    statusFormatPassword: false,
    statusPassword: false,
    show2: false,
    showUsernameTooltip: false,
    showFirstnameTooltip: false,
    showLastnameTooltip: false,
    showEmailTooltip: false,
    showPhoneTooltip: false,
    showSocialUserIDTooltip: false,
    showRecipientIDTooltip: false,
    showRecipientInstagramTooltip: false,
    showGroupModelTooltip: false,
    showModelTooltip: false,
    showQuickModelTooltip: false,
    showPasswordTooltip: false,
    showConfirmPasswordTooltip: false,
    images: {
      edit: editImg,
      delete: deleteImg,
      basicInfoIcon: basicInfoIcon,
      socialIntegrationIcon: socialIntegrationIcon,
      modelConfigIcon: modelConfigIcon,
      newSaveImg: newSaveImg,
    },
  }),
  watch: {
    options: {
      handler() {
        if (!this.isInitialized) return;
        this.Get();
      },
      deep: true,
    },
    roleFilterMenuOpen(value) {
      if (value) {
        this.tempSelectedRole = this.selectedRoleFilter;
      }
    },
    searchGroupModel(val) {
      this.GetGroupModel(val);
    },
    "edit.password"() {
      let self = this;
      if (
        self.edit.password != null &&
        self.edit.password != undefined &&
        self.edit.password != ""
      ) {
        self.checkLengthPassword = self.edit.password.length >= 8;
        self.checkLowercaseLetter = /[a-z]/.test(self.edit.password);
        self.checkUppercaseLetter = /[A-Z]/.test(self.edit.password);
        self.checkNumber = /\d/.test(self.edit.password);
        // self.checkSpecialCharacter = /[!@#$%^&*(),.?":{}|<>]/.test(self.edit.password);
        self.checkSpecialCharacter = /[^a-zA-Z0-9ก-๙]/.test(self.edit.password);
        if (
          self.checkLengthPassword == true &&
          self.checkLowercaseLetter == true &&
          self.checkUppercaseLetter == true &&
          self.checkNumber == true &&
          self.checkSpecialCharacter == true
        ) {
          self.statusFormatPassword = true;
        } else {
          self.statusFormatPassword = false;
        }
        if (self.edit.password == self.edit.confirmpassword) {
          self.checkPasswordMatch = true;
        } else if (
          self.edit.confirmpassword != null &&
          self.edit.confirmpassword.length > 0
        ) {
          self.checkPasswordMatch = false;
        }
      }
    },
    "edit.confirmpassword"() {
      let self = this;
      self.checkPasswordMatch = self.edit.password == self.edit.confirmpassword;
      if (
        self.edit.password != null &&
        self.edit.confirmpassword != null &&
        self.edit.password == self.edit.confirmpassword &&
        self.checkLengthPassword == true &&
        self.checkLowercaseLetter == true &&
        self.checkUppercaseLetter == true &&
        self.checkNumber == true &&
        self.checkSpecialCharacter == true
      ) {
        self.statusPassword = true;
      } else {
        self.statusPassword = false;
      }
    },
  },
  mounted() {
    // this.permission = this.checkPermissionFour(1, 2, 3, 4);
    this.loadingTable = true;
    this.dialog = true;
    this.groupID = this.$route.query.id;
    store.state.groupSecurityID = this.$route.query.id;
    if (
      this.groupID != null &&
      this.groupID != undefined &&
      this.groupID != ""
    ) {
      this.headers = [
        {
          title: this.$t("no"),
          align: "left",
          sortable: false,
        },
        {
          title: this.$t("username"),
          align: "left",
          sortable: true,
          value: "username",
        },
        {
          title: this.$t("email"),
          align: "left",
          sortable: true,
          value: "email",
        },
        {
          title: this.$t("phonenumber"),
          align: "left",
          sortable: true,
          value: "mobile",
        },
        {
          title: this.$t("fullName"),
          align: "left",
          sortable: true,
          value: "fullName",
        },
        {
          title: this.$t("manage"),
          align: "left",
          sortable: false,
          width: "150px",
        },
      ];
      Promise.all([
        this.GetDate(),
        this.GetGroupModel(),
        this.GetModel(),
        this.GetDDModelQuickGroup(),
      ]).then(() => {
        this.dialog = false;
        this.Get();
        this.isInitialized = true;
      });
    } else {
      this.$router.push({
        name: "GroupUserManagement",
      });
    }
  },
  computed: {
    passwordIsValid() {
      return (
        this.checkLengthPassword &&
        this.checkLowercaseLetter &&
        this.checkUppercaseLetter &&
        this.checkNumber &&
        this.checkSpecialCharacter &&
        this.checkPasswordMatch &&
        this.edit.password &&
        this.edit.confirmpassword
      );
    },
    filteredUsers() {
      let users = this.userList.filter((d) => {
        return Object.keys(this.filters).every((f) => {
          return this.filters[f].length < 1 || this.filters[f].includes(d[f]);
        });
      });

      if (this.selectedRoleFilter !== null) {
        users = users.filter((user) => user.roleId === this.selectedRoleFilter);
      }

      return users;
    },
    filteredRoleCount() {
      if (this.selectedRoleFilter === null) {
        return this.searchTotalTable;
      }
      return this.userList.filter((user) => user.roleId === this.selectedRoleFilter).length;
    },
    form() {
      return {
        username: this.user.username,
        firstname: this.user.firstname,
        lastname: this.user.lastname,
        email: this.user.email,
        phone: this.user.phone,
        password: this.user.password,
      };
    },
    headersTemplate() {
      return [
        { text: "CSV", align: "center" },
        { text: "Excel", align: "center" },
      ];
    },
  },

  methods: {
    // Model selection helper methods
    isModelSelected(modelDisplayID) {
      return Array.isArray(this.edit.model) && this.edit.model.includes(modelDisplayID);
    },
    removeModel(modelDisplayID) {
      if (this.isAllModelsSelected(this.edit.model)) {
        this.edit.model = [];
      } else if (Array.isArray(this.edit.model)) {
        this.edit.model = this.edit.model.filter(id => id !== modelDisplayID);
      }
    },
    toggleAllModels() {
      const allIds = (this.listDDModel || []).map((model) => model.modelDisplayID);
      if (!allIds.length) {
        return;
      }
      if (this.isAllModelsSelected(this.edit.model)) {
        this.edit.model = [];
      } else {
        this.edit.model = allIds.slice();
      }
    },
    isAllModelsSelected(selectedModels) {
      const allIds = (this.listDDModel || []).map((model) => model.modelDisplayID);
      if (!Array.isArray(selectedModels) || selectedModels.length === 0) {
        return false;
      }
      if (selectedModels.length !== allIds.length) {
        return false;
      }
      return allIds.every((id) => selectedModels.includes(id));
    },
    shouldCollapseModelChips(selectedModels) {
      return this.isAllModelsSelected(selectedModels);
    },
    getRoleFilterLabel(roleId) {
      if (roleId === null) {
        return "All Roles";
      }
      const matchedRole = this.roleFilterOptions.find((option) => option.value === roleId);
      return matchedRole ? matchedRole.label : "All Roles";
    },
    cancelApplyFilter() {
      this.applyFilterDialogOpen = false;
    },
    confirmApplyFilter() {
      this.selectedRoleFilter = this.tempSelectedRole;
      this.applyFilterDialogOpen = false;
      this.roleFilterMenuOpen = false;
      this.Search();
    },
    showWarningAlert(title, messageHtml) {
      const rawOkLabel = this.$t ? this.$t("ok") : "OK";
      const confirmLabel = rawOkLabel && rawOkLabel.toString().trim().length > 0
        ? rawOkLabel
        : "OK";

      return this.$swal.fire({
        title,
        html: messageHtml,
        icon: "warning",
        showCloseButton: true,
        focusConfirm: true,
        buttonsStyling: false,
        allowOutsideClick: false,
        confirmButtonText: confirmLabel,
        customClass: {
          popup: "swal-warning-popup",
          title: "swal-warning-title",
          htmlContainer: "swal-warning-body",
          actions: "swal-warning-actions",
          confirmButton: "swal-warning-confirm",
          closeButton: "swal-warning-close",
        },
      });
    },
    GetGroupModel() {
      let self = this;
      self.$call.GetDDGroupModel(
        (dataResponse) => {
          if (dataResponse.status == 0) {
            self.listDDGroupModel = dataResponse.data;
          } else {
            self.listDDGroupModel = [];
          }
        },
        (errorResource) => {
          console.error(errorResource);
        }
      );
    },
    GetDDModelQuickGroup() {
      let self = this;
      self.$call.GetDDModelQuickGroup(
        (dataResponse) => {
          if (dataResponse.status == 0) {
            self.DDQuickModel = dataResponse.data;
          } else {
            self.DDQuickModel = [];
          }
        },
        (errorResource) => {
          console.error(errorResource);
        }
      );
    },
    Sync() {
      let self = this;
      self.dialog = true;
      self.$call.SyncDataEntraIDGroup(
        self.groupID,
        (dataResponse) => {
          if (dataResponse.status == 0) {
            self.$swal
              .fire({
                title: " Successfully Sync !",
                showConfirmButton: false,
                timer: 1500,
                customClass: {
                  popup: 'success-banner',
                  title: 'success-banner-title'
                }
              })
              .then(() => {
                self.Get();
              });
          } else {
            self.showWarningAlert("Warning", dataResponse.data);
            // self.$swal.fire({
            //   icon: "warning",
            //   title: dataResponse.data,
            //   confirmButtonColor: "#3085d6",
            //   confirmButtonText: "Close",
            // });
          }
          self.dialog = false;
        },
        (err) => {
          console.log(err);
          self.dialog = false;
        }
      );
    },

    ConfirmProcess() {
      let self = this;
      self.$swal
        .fire({
          icon: "warning",
          title: this.$t("importUserTitle"),
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: this.$t("yes"),
          cancelButtonText: this.$t("cancel"),
        })
        .then((result) => {
          if (result.isConfirmed) {
            self.ImportUser();
          }
        });
    },

    PhoneNumberValidation(phno) {
      let phoneno = /^\d{10}$/;
      return phoneno.test(phno) || "Invalid Phone format";
    },

    validateEmail(email) {
      // eslint-disable-next-line no-useless-escape
      let re =
        /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

      return re.test(email) || "invalid email";
    },
    Search() {
      let self = this;
      self.isSearch = true;
      self.options.page = 1;
      self.Get();
    },
    GetDate() {
      let today1 = new Date();
      let dd = today1.getDate();
      let mm = today1.getMonth() + 1; //January is 0!

      let yyyy = today1.getFullYear();
      if (dd < 10) {
        dd = "0" + dd;
      }
      if (mm < 10) {
        mm = "0" + mm;
      }

      this.today = dd + "/" + mm + "/" + yyyy;
    },

    GetModel() {
      let self = this;
      self.$call.DDGPTModel(
        (dataResponse) => {
          if (dataResponse.status == 0) {
            self.listDDModel = dataResponse.data;
          } else {
            self.listDDModel = [];
          }
        },
        (errorResource) => {
          console.error(errorResource);
        }
      );
    },

    Get() {
      try {
        let self = this;
        // self.dialog = true;
        self.loadingTable = true;

        let { page, itemsPerPage, sortBy } = self.options;
        let sortField = null;
        let sortOrder = null;

        if (sortBy && sortBy.length > 0) {
          sortField = sortBy[0].key;
          sortOrder = sortBy[0].order === "desc";
        }
        let temp = {
          page: self.isSearch ? 1 : page,
          perPage: itemsPerPage,
          username: self.username,
          eMail: self.email,
          sortDesc: sortOrder,
          sortBy: sortField,
          roleId: self.selectedRoleFilter !== null ? [self.selectedRoleFilter] : [],
          groupID: self.groupID,
        };
        self.$call.GetTableUserGroupManagement(
          temp,
          (dataResponse) => {
            if (dataResponse.status == 0) {
              self.searchTotalTable = dataResponse.data.total;
              self.userList = dataResponse.data.data;
              self.totalTable = self.isFirst
                ? dataResponse.data.total
                : self.totalTable;
              self.AutoRefresh();
            } else {
              self.userList = [];
              self.totalTable = 0;
              self.searchTotalTable = 0;
            }
            // self.dialog = false;
            self.loadingTable = false;
            self.isSearch = false;
            self.isFirst = false;
          },
          (errorResource) => {
            // self.dialog = false;
            self.loadingTable = false;
            self.isSearch = false;
            console.error(errorResource);
          }
        );
      } catch (error) {
        this.LogTrace(error, "Get");
      }
    },
    ReFreshGet() {
      try {
        let self = this;
        self.dialog = true;
        self.loadingTable = true;
        const { page, itemsPerPage } = this.options;
        self.sortBy =
          self.options.sortBy.length == 0 ? null : self.options.sortBy[0];
        self.sortDesc =
          self.options.sortDesc.length == 0 ? null : self.options.sortDesc[0];
        let temp = {
          page: self.isSearch ? 1 : page,
          perPage: itemsPerPage != -1 ? itemsPerPage : self.totalTable,
          username: self.username,
          eMail: self.email,
          sortDesc: self.sortDesc,
          sortBy: self.sortBy,
          roleId: self.selectedRoleFilter !== null ? [self.selectedRoleFilter] : [],
          groupID: self.groupID,
        };
        self.$call.GetTableUserGroupManagement(
          temp,
          (dataResponse) => {
            if (dataResponse.status == 0) {
              self.searchTotalTable = dataResponse.data.total;
              self.userList = dataResponse.data.data;
              self.totalTable = dataResponse.data.total;
            } else {
              self.userList = [];
              self.totalTable = 0;
              self.searchTotalTable = 0;
            }
            self.dialog = false;
            self.loadingTable = false;
            self.isSearch = false;
            self.isFirst = false;
          },
          (errorResource) => {
            self.dialog = false;
            self.loadingTable = false;
            self.isSearch = false;
            console.error(errorResource);
          }
        );
      } catch (error) {
        this.dialog = false;
        this.loadingTable = false;
        this.LogTrace(error, "Get");
      }
    },
    async AutoRefresh() {
      let self = this;
      let death_waiting = true;
      // self.StatusSync = true;
      do {
        for (let value of self.userList) {
          if (value.inProcess < value.inProcessMax && !value.isError) {
            self.ReFreshGet();
            self.StatusSync = true;
            death_waiting = true;
            break;
          } else {
            death_waiting = false;
            self.StatusSync = false;
          }
        }
        if (window.location.pathname != "/UserGroupDetail") {
          death_waiting = false;
        }
        console.log("Wait for 5 seconds!");
        await new Promise((resolve) => setTimeout(resolve, 5000));
      } while (death_waiting);
    },
    EditUser(temp) {
      let self = this;
      //self.oldPassword = self.decryptShiftData(temp.password, 7);
      self.Editdialog = true;
      self.edit.id = temp.userId;
      self.edit.username = temp.username;
      self.edit.firstname = temp.firstName;
      self.edit.lastname = temp.lastName;
      // self.edit.password = temp.password;
      // self.edit.password = self.decryptShiftData(temp.password, 7);
      // self.edit.employeename = temp.employeeName;
      self.edit.roleId =
        temp.roleId == null || temp.roleId == undefined ? 2 : temp.roleId;
      self.edit.userType = temp.userType;
      self.edit.email = temp.email;
      self.edit.phone = temp.mobile;
      self.edit.modelQuickGroupID = temp.modelQuickGroupID;
      self.edit.socialUserID = temp.socialUserID;
      self.edit.recipientID = temp.recipientID;
      self.edit.recipientId_Instagram = temp.recipientId_Instagram;
      self.edit.isUserFromLine = temp.isUserFromLine;
      if (temp.groupModel == null) {
        self.edit.groupModel = null;
      } else {
        self.edit.groupModel = parseInt(temp.groupModel);
      }
      if (temp.model == null) {
        self.edit.model = [];
      } else {
        self.edit.model = temp.model;
      }
    },
    UpdateUser() {
      try {
        let self = this;
        self.dialog = true;
        let emailvali = self.validateEmail(self.edit.email);
        if (
          self.edit.username != "" &&
          self.edit.lastname != "" &&
          self.edit.firstname != "" &&
          self.edit.email != "" &&
          self.edit.password != "" &&
          (self.edit.groupModel != null || self.edit.model.length != 0) &&
          emailvali
        ) {
          let tempUser = {
            userId: self.edit.id,
            username: self.edit.username,
            password: self.edit.password,
            idcard: 0,
            prefixId: 0,
            firstName: self.edit.firstname,
            lastName: self.edit.lastname,
            roleId: self.edit.roleId,
            mobile: self.edit.phone,
            eMail: self.edit.email,
            groupModel: self.edit.groupModel,
            model: self.edit.model,
            modelQuickGroupID: self.edit.modelQuickGroupID,
            socialUserID: self.edit.socialUserID,
            recipientID: self.edit.recipientID,
            recipientId_Instagram: self.edit.recipientId_Instagram,
          };
          self.$call.UpdateUserChat(
            tempUser,
            (dataResponse) => {
              if (dataResponse.status == 0) {
                if (Cookies.get("userId") == self.edit.id) {
                  Cookies.set(
                    "fullName",
                    `${self.edit.firstname} ${self.edit.lastname}`
                  );
                  store.state.fullName = `${self.edit.firstname} ${self.edit.lastname}`;
                  Cookies.set("username", self.edit.username);
                  if (self.oldPassword != self.edit.password) {
                    self.$call.ClearUserInfo();
                    self.$router.push("/login");
                  }
                }
                self.$swal.fire({
                  title: "✔ Successfully Updated !",
                  showConfirmButton: false,
                  timer: 1500,
                  customClass: {
                    popup: 'success-banner',
                    title: 'success-banner-title'
                  },
                });
                // self.$swal.fire({
                //   icon: "success",
                //   title: dataResponse.message,
                //   showConfirmButton: false,
                //   timer: 1500,
                // });
                self.Editdialog = false;
                self.edit.name = null;
                self.edit.surname = null;
                self.edit.email = null;
                self.edit.department = null;
                self.edit.section = null;
                self.edit.status = null;
                self.edit.roleId = null;
                self.edit.userType = null;
                self.edit.model = null;

                self.Get();
              } else {
                self.$swal.fire({
                  icon: "warning",
                  title: dataResponse.data.message,
                  showConfirmButton: false,
                  timer: 1800,
                });
              }
              self.dialog = false;
            },
            (errorResource) => {
              self.dialog = false;
              console.error(errorResource);
            }
          );
        } else {
          if (emailvali != true /*|| phonevali != true*/) {
            self.$swal.fire({
              icon: "warning",
              title: "please fill correct format!",
              showConfirmButton: false,
              timer: 2000,
            });
          } else if (
            self.edit.username == "" ||
            self.edit.firstname == "" ||
            self.edit.lastname == "" ||
            self.edit.roleId == "" ||
            self.edit.userType == "" ||
            self.edit.groupModel == null ||
            self.edit.model.length < 1
          ) {
            self.$swal.fire({
              icon: "warning",
              title: "please fill all fields!!!",
              showConfirmButton: false,
              timer: 2000,
            });
          } else if (self.user.roleId == null) {
            self.$swal.fire({
              icon: "warning",
              title: "Please fill correct format!",
              showConfirmButton: false,
              timer: 2000,
            });
          }
          self.formHasErrors = true;
          self.Editdialog = true;
          self.dialog = false;
        }
      } catch (error) {
        this.LogTrace(error, "UpdateUser");
      }
    },
    DeleteUser(user) {
      let self = this;
      let id = user.userId;
      let groupID = self.groupID;
      try {
        if (user.roleId == 0 && self.roleId != "0") {
          self.$swal.fire({
            icon: "warning",
            title: this.$t("notAuthoritySuperAdmin"),
            showConfirmButton: false,
            timer: 2000,
          });
          return;
        }
        self.$swal
          .fire({
            title: this.$t("confirmDeletion"),
            html: "Do you want to confirm deletion of this user? This action cannot be undone.",
            icon: "warning",
            showCloseButton: true,
            showCancelButton: true,
            cancelButtonText: this.$t("cancel"),
            confirmButtonText: `<span class="swal-delete-btn-content"><img src="${deleteAlertIcon}" alt="" class="swal-delete-btn-icon" />${this.$t("delete")}</span>`,
            reverseButtons: false,
            focusCancel: true,
            buttonsStyling: false,
            customClass: {
              popup: "swal-delete-popup",
              title: "swal-delete-title",
              htmlContainer: "swal-delete-body",
              actions: "swal-delete-actions",
              confirmButton: "swal-delete-confirm",
              cancelButton: "swal-delete-cancel",
            },
            // title: this.$t("confirmDeletion"),
            // text: this.$t("confirmDeletionUser"),
            // icon: "warning",
            // showCancelButton: true,
            // confirmButtonColor: "#3085d6",
            // cancelButtonColor: "#d33",
            // confirmButtonText: this.$t("confirm"),
            // cancelButtonText: this.$t("cancel"),
            // reverseButtons: false,
          })
          .then((result) => {
            if (result.isConfirmed) {
              self.dialog = true;
              let temp = {
                id: id,
                groupid: groupID,
              };
              self.$call.DeleteUserByID(
                temp,
                (dataResponse) => {
                  if (dataResponse.status == 0) {
                    self.totalTable = self.totalTable - 1;
                    self.Get();
                    self.$swal.fire({
                      title: "✔ Successfully Deleted !",
                      showConfirmButton: false,
                      timer: 1500,
                      customClass: {
                        popup: 'success-banner',
                        title: 'success-banner-title'
                      },
                    });
                    // self.$swal.fire({
                    //   icon: "success",
                    //   title: dataResponse.message,
                    //   showConfirmButton: false,
                    //   timer: 1500,
                    // });
                  } else if (dataResponse.status == 4) {
                    self.$swal.fire({
                      icon: "warning",
                      title: this.$t("oneSuperAdmin"),
                      showConfirmButton: false,
                      timer: 2000,
                    });
                  }
                  self.dialog = false;
                },
                (errorResource) => {
                  self.dialog = false;
                  console.error(errorResource);
                }
              );
            }
          });
      } catch (error) {
        this.LogTrace(error, "DeleteUser");
      }
    },
    decryptData(encryptedText, key) {
      const decryptedBytes = CryptoJS.AES.decrypt(encryptedText, key);
      const decryptedText = decryptedBytes.toString(CryptoJS.enc.Utf8);
      return decryptedText;
    },
    decryptShiftData(encryptedText, shift) {
      let chars = encryptedText.split("");
      let decryptedChars = [];

      for (let i = 0; i < chars.length; i++) {
        let c = chars[i];

        if (/[a-zA-Z]/.test(c)) {
          let offset =
            c === c.toUpperCase() ? "A".charCodeAt(0) : "a".charCodeAt(0);
          c = String.fromCharCode(
            ((c.charCodeAt(0) - offset - shift + 26) % 26) + offset
          );
        } else if (/[0-9]/.test(c)) {
          c = String.fromCharCode(
            ((c.charCodeAt(0) - "0".charCodeAt(0) - shift + 10) % 10) +
            "0".charCodeAt(0)
          );
        }

        decryptedChars.push(c);
      }

      return decryptedChars.join("");
    },
    closeChangePassword() {
      let self = this;
      self.edit.password = null;
      self.edit.confirmpassword = null;
      self.checkLengthPassword = true;
      self.checkLowercaseLetter = true;
      self.checkUppercaseLetter = true;
      self.checkNumber = true;
      self.checkSpecialCharacter = true;
      self.checkPasswordMatch = true;
      self.changePasswordDialog = false;
      self.show1 = false;
      self.show2 = false;
    },
    saveChangePassword() {
      try {
        let self = this;
        self.dialog = true;
        if (
          self.edit.password != "" &&
          self.edit.confirmpassword != "" &&
          self.edit.password != null &&
          self.edit.confirmpassword != null &&
          self.edit.password != undefined &&
          self.edit.confirmpassword != undefined &&
          self.edit.password == self.edit.confirmpassword &&
          self.checkPasswordMatch == true &&
          self.checkLengthPassword &&
          self.checkLowercaseLetter &&
          self.checkUppercaseLetter &&
          self.checkNumber &&
          self.checkSpecialCharacter
        ) {
          let temp = {
            userID: self.edit.id,
            password: self.edit.password,
          };
          self.$call.SaveChangePasswordUserChat(
            temp,
            (dataResponse) => {
              if (dataResponse.status == 0) {
                self.$swal.fire({
                  icon: "success",
                  title: dataResponse.message,
                  showConfirmButton: false,
                  timer: 1500,
                });
                self.edit.password = null;
                self.edit.confirmpassword = null;
                self.checkLengthPassword = true;
                self.checkLowercaseLetter = true;
                self.checkUppercaseLetter = true;
                self.checkNumber = true;
                self.checkSpecialCharacter = true;
                self.checkPasswordMatch = true;
                self.changePasswordDialog = false;
                self.show1 = false;
                self.show2 = false;
              } else {
                self.$swal.fire({
                  icon: "warning",
                  title: dataResponse.data.message,
                  showConfirmButton: false,
                  timer: 1800,
                });
              }
              self.dialog = false;
            },
            (errorResource) => {
              self.dialog = false;
              console.error(errorResource);
            }
          );
        } else {
          if (self.edit.password != null && self.edit.confirmpassword == null) {
            self.$swal.fire({
              icon: "warning",
              title: self.$t("warning"),
              text: self.$t("Please fill Confirm Password"),
              showConfirmButton: false,
              timer: 2000,
            });
          } else if (
            self.edit.password == null &&
            self.edit.confirmpassword != null
          ) {
            self.$swal.fire({
              icon: "warning",
              title: self.$t("warning"),
              text: self.$t("Please fill Password"),
              showConfirmButton: false,
              timer: 2000,
            });
          } else if (
            self.edit.password == null &&
            self.edit.confirmpassword == null
          ) {
            self.$swal.fire({
              icon: "warning",
              title: self.$t("warning"),
              text: self.$t("Please fill Password and Confirm Password"),
              showConfirmButton: false,
              timer: 2000,
            });
          } else if (
            !self.checkLengthPassword ||
            !self.checkLowercaseLetter ||
            !self.checkUppercaseLetter ||
            !self.checkNumber ||
            !self.checkSpecialCharacter
          ) {
            self.$swal.fire({
              icon: "warning",
              title: self.$t("warning"),
              text: self.$t("Please fill correct format!"),
              showConfirmButton: false,
              timer: 2000,
            });
          } else if (
            self.edit.password != self.edit.confirmpassword &&
            self.checkPasswordMatch == false
          ) {
            self.$swal.fire({
              icon: "warning",
              title: self.$t("warning"),
              text: self.$t(
                "Password and Confirm Password do not match.Please fill correctly."
              ),
              showConfirmButton: false,
              timer: 2000,
            });
          } else {
            self.$swal.fire({
              icon: "warning",
              title: self.$t("warning"),
              text: self.$t("Please fill all the fields!!!"),
              showConfirmButton: false,
              timer: 2000,
            });
          }
          self.changePasswordDialog = true;
          self.dialog = false;
        }
      } catch (error) {
        this.LogTrace(error, "saveChangePassword");
      }
    },
  },
};
</script>
<style scoped>
/* Base Layout */
.user-group-detail-page {
  min-height: 100vh;
  background-color: #f8f9fa;
}

.user-group-detail-container {
  padding: 24px;
}

.main-content-card {
  border-radius: 16px !important;
  border: 1px solid #dcdcdc !important;
  background: #ffffff !important;
  overflow: hidden;
  padding: 20px;
}

.page-header-section {
  width: 100%;
}

.page-header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  width: 100%;
}

.page-title-wrapper {
  flex-shrink: 0;
}

.page-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
  line-height: 1.3;
}

.header-actions-group {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.btn-label {
  font-size: 0.9rem;
  font-weight: 600;
}

.action-btn-sync {
  border-radius: 12px !important;
  background: linear-gradient(90deg, #4caf50 0%, #2e7d32 100%) !important;
  color: #ffffff !important;
  font-weight: 600;
  text-transform: none;
  padding: 0 24px !important;
  height: 44px !important;
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3) !important;
  transition: all 0.2s ease !important;
}

.action-btn-sync:hover:not(:disabled) {
  background: #2ABF56 !important;
  transform: translateY(-1px);
}

.action-btn-sync:disabled {
  opacity: 0.7;
  box-shadow: none !important;
  transform: none !important;
}

.action-btn-setup {
  border-radius: 12px !important;
  background: linear-gradient(90deg, #C32E0E 0%, #F67760 100%) !important;
  color: #ffffff !important;
  font-weight: 600;
  text-transform: none;
  padding: 0 24px !important;
  height: 44px !important;
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3) !important;
  transition: all 0.2s ease !important;
}

.action-btn-setup:hover {
  background: linear-gradient(90deg, #F36243 0%, #FF8E7A 100%) !important;
  box-shadow: 0 6px 16px rgba(243, 98, 67, 0.4) !important;
  transform: translateY(-1px);
}

/* Search Panel */
.search-panel-section {
  width: 100%;
  padding: 10px 20px;
  border-radius: 16px;
  border: 1px solid var(--placeholder, #ccc);
  margin-bottom: 20px;
  background: #ffffff;
}

.custom-search-input-wrapper-full {
  width: 100%;
  flex: 1;
}

.custom-search-input-container {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #ffffff;
  border-radius: 14px;
  border: 1px solid #dcdcdc;
  padding: 10px 14px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.custom-search-input-container:focus-within {
  border-color: #c32e0e;
  box-shadow: 0 4px 14px rgba(195, 46, 14, 0.18);
}

.custom-search-input-icon {
  color: #872612;
  margin-right: 10px;
  font-size: 24px;
}

.custom-search-input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 16px;
  color: rgba(0, 0, 0, 0.87);
  background: transparent;
  height: 100%;
  max-width: unset !important;
}

.clear-icon {
  cursor: pointer;
  color: #bdbdbd;
  transition: color 0.2s ease;
}

.clear-icon:hover {
  color: #c32e0e;
}

/* Search Button */
.search-btn-circular {
  background: linear-gradient(90deg, #c32e0e 0%, #f67760 100%) !important;
  box-shadow: 0 4px 12px rgba(195, 46, 14, 0.3) !important;
  transition: all 0.2s ease;
}

.search-btn-circular:hover {
  box-shadow: 0 6px 16px rgba(195, 46, 14, 0.4) !important;
  transform: scale(1.05);
}

.vertical-divider {
  width: 1px;
  height: 40px;
  background-color: #ccc;
}

.filter-label {
  font-size: 16px;
  font-weight: 500;
  color: #000000;
}

.filter-icon-btn-circular {
  background: transparent !important;
  border-radius: 50% !important;
  width: 40px !important;
  height: 40px !important;
  min-width: 40px !important;
  box-shadow: none !important;
  transition: all 0.2s ease !important;
}

.filter-icon-btn-circular:hover {
  background-color: #fde4df !important;
  border: 2px solid #fde4df !important;
}

.role-filter-dropdown {
  border-radius: 12px !important;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12) !important;
  overflow: hidden;
}

.role-filter-item {
  min-height: 44px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.role-filter-item:hover {
  background-color: #f5f5f5;
}

.role-filter-item-title {
  font-size: 14px;
  font-weight: 400;
  color: #333;
}

.selected-role-item .role-filter-item-title {
  color: #c32e0e;
  font-weight: 500;
}

.apply-filter-btn {
  background: linear-gradient(90deg, #c32e0e 0%, #f67760 100%) !important;
  color: white !important;
  font-weight: 500;
  text-transform: none;
  letter-spacing: 0;
  border-radius: 20px !important;
  height: 40px !important;
}

.apply-filter-btn:hover {
  opacity: 0.9;
}

.apply-filter-confirm-card {
  border-radius: 16px !important;
}

.apply-filter-confirm-title {
  font-size: 18px !important;
  font-weight: 600 !important;
  color: #171717;
  border-bottom: 1px solid rgba(201, 201, 201, 0.6);
}

.apply-filter-confirm-actions {
  border-top: 1px solid rgba(201, 201, 201, 0.6);
}

.apply-filter-cancel-btn,
.apply-filter-apply-btn {
  text-transform: none;
  font-weight: 500;
}

.clear-filters-btn {
  text-transform: none;
  font-weight: 500;
}

.tooltip-content {
  max-width: 300px;
  white-space: normal;
}

/* Table Styling */
.user-group-data-table {
  border-radius: 16px;
  overflow: hidden;
}

.user-group-data-table :deep(thead) {
  background-color: #fff2f2 !important;
  color: #171717 !important;
  font-weight: 800 !important;
}

/* Force sticky header cells to keep the custom background after data loads */
.user-group-data-table :deep(.v-table__wrapper > table > thead > tr > th) {
  background-color: #fff2f2 !important;
  color: #171717 !important;
}

.v-table--has-top :deep(.v-table__wrapper) {
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
}

.user-group-data-table :deep(.v-data-table-header__content span) {
  font-weight: 600 !important;
}

.user-group-data-table :deep(.v-data-table-footer .v-field) {
  border-radius: 10px !important;
  width: 70px !important;
}

.user-group-data-table :deep(.mdi-menu-down.mdi.v-icon.notranslate.v-theme--light.v-icon--size-default.v-select__menu-icon) {
  color: #872612;
  opacity: 1 !important;
}

.user-group-data-table :deep(tbody tr) {
  transition: background-color 0.15s ease;
}

.user-group-data-table :deep(tbody tr:hover) {
  background-color: rgba(195, 46, 14, 0.04);
}

.table-footer-wrapper {
  font-weight: 500;
  color: #424242;
}

/* Utility */
.pass {
  display: none;
}

.failed {
  display: block;
  color: red;
}

/* Action Buttons in Table */
.action-group {
  display: flex;
  align-items: center;
  gap: 4px;
}

.action-divider {
  width: 1px;
  height: 20px;
  background: rgba(0, 0, 0, 0.12);
  margin: 0 4px;
}

/* No Data Template */
.no-data-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
  padding: 40px 20px;
}

.no-data-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 400px;
  gap: 24px;
}

.no-data-icon {
  opacity: 0.6;
  transition: opacity 0.3s ease;
}

.no-data-text {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.no-data-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #424242;
  margin: 0;
  letter-spacing: 0.025em;
}

.no-data-subtitle {
  font-size: 0.9rem;
  color: #757575;
  margin: 0;
  font-weight: 400;
  line-height: 1.4;
}

.no-data-btn {
  border-radius: 8px !important;
  font-weight: 500;
  text-transform: none;
  letter-spacing: 0.025em;
  min-width: 140px;
  height: 40px;
}

.no-data-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(25, 118, 210, 0.3);
}

/* Dialog Styles */
.edit-user-dialog,
.change-password-dialog {
  border-radius: 16px !important;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  animation: slideInDown 0.3s ease-out;
}

.edit-user-header,
.change-password-header {
  color: white;
  padding: 20px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.edit-user-header {
  background: linear-gradient(135deg, #2196f3 0%, #1976d2 100%);
}

.change-password-header {
  background: linear-gradient(135deg, #ff9800 0%, #f57c00 100%);
}

.header-content {
  display: flex;
  align-items: center;
  gap: 16px;
}

.header-icon {
  font-size: 28px;
  opacity: 0.9;
}

.header-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.dialog-title {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
  line-height: 1.2;
  color: white;
}

.dialog-subtitle {
  font-size: 0.875rem;
  opacity: 0.8;
  margin: 0;
  line-height: 1.3;
  color: white;
}

.close-btn {
  color: rgba(255, 255, 255, 0.8) !important;
  transition: all 0.2s ease;
}

.close-btn:hover {
  color: white !important;
  background-color: rgba(255, 255, 255, 0.1) !important;
}

.edit-user-content,
.change-password-content {
  padding: 24px;
  background-color: #fafafa;
  max-height: 70vh;
  overflow-y: auto;
}

/* Form Sections */
.form-section {
  margin-bottom: 24px;
  transition: all 0.3s ease;
}

.section-title {
  display: flex;
  align-items: center;
  font-size: 1.1rem;
  font-weight: 600;
  color: rgb(var(--v-theme-on-surface));
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 2px solid rgba(var(--v-theme-primary), 0.1);
}

.form-row {
  margin-bottom: 8px;
}

.form-row .v-col {
  padding-top: 8px;
  padding-bottom: 8px;
}

/* Input Styling */
.input-wrapper {
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;
  min-height: 60px;
}

.input-wrapper:has(.password-requirements) {
  min-height: 200px;
}

.input-wrapper:has(.password-match) {
  min-height: 120px;
}

.input-label {
  font-size: 0.875rem;
  font-weight: 400;
  color: #171717;
  opacity: 0.87;
  line-height: 1.2;
}

.modern-input :deep(.v-field) {
  background: white;
  border-radius: 16px !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
}

.modern-input :deep(.v-field--focused) {
  box-shadow: 0 2px 8px rgba(var(--v-theme-primary), 0.15);
}

.modern-input :deep(.v-field__outline) {
  --v-field-border-opacity: 0.25;
}

.modern-input :deep(.v-field--focused .v-field__outline) {
  --v-field-border-opacity: 1;
  --v-field-border-width: 2px;
}

.change-password-content .modern-input :deep(.v-field--focused) {
  box-shadow: 0 2px 8px rgba(255, 152, 0, 0.15);
}

.change-password-content .modern-input :deep(.v-field--focused .v-field__outline) {
  border-color: #ff9800;
}

.help-icon {
  color: rgba(var(--v-theme-primary), 0.7);
  cursor: help;
  transition: color 0.2s ease;
}

.help-icon:hover {
  color: rgb(var(--v-theme-primary));
}

/* Password Requirements */
.password-requirements,
.password-match {
  margin-top: 8px;
  background: white;
  border-radius: 8px;
  border: 1px solid rgba(var(--v-border-color), 0.12);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.password-requirements {
  padding: 12px;
  overflow: hidden;
  min-height: 120px;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
}

.password-match {
  padding: 8px 12px;
  min-height: 40px;
  display: flex;
  align-items: center;
}

.requirement-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.8rem;
  margin: 4px 0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  padding: 4px 6px;
  border-radius: 6px;
  opacity: 0;
  transform: translateX(-10px);
  animation: slideInRequirement 0.4s ease-out forwards;
}

.requirement-item:nth-child(1) {
  animation-delay: 0.1s;
}

.requirement-item:nth-child(2) {
  animation-delay: 0.15s;
}

.requirement-item:nth-child(3) {
  animation-delay: 0.2s;
}

.requirement-item:nth-child(4) {
  animation-delay: 0.25s;
}

.requirement-item:nth-child(5) {
  animation-delay: 0.3s;
}

.requirement-item.valid {
  color: rgb(var(--v-theme-success));
  background: rgba(var(--v-theme-success), 0.08);
  border-left: 3px solid rgb(var(--v-theme-success));
}

.requirement-item:not(.valid) {
  color: rgb(var(--v-theme-error));
  background: rgba(var(--v-theme-error), 0.08);
  border-left: 3px solid rgb(var(--v-theme-error));
}

/* Security Tips */
.security-tips {
  margin-top: 20px;
  animation: fadeInUp 0.5s ease-out 0.3s both;
}

.security-alert {
  border-radius: 12px;
  border: 1px solid rgba(var(--v-theme-info), 0.2);
  overflow: hidden;
  transition: all 0.3s ease;
}

.security-alert:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(var(--v-theme-info), 0.15);
}

.alert-content {
  font-size: 0.875rem;
}

.tips-list {
  margin: 8px 0 0 16px;
  padding: 0;
}

.tips-list li {
  margin: 4px 0;
  line-height: 1.4;
  color: rgba(var(--v-theme-on-surface), 0.8);
  opacity: 0;
  transform: translateX(-10px);
  animation: slideInTip 0.4s ease-out forwards;
}

.tips-list li:nth-child(1) {
  animation-delay: 0.9s;
}

.tips-list li:nth-child(2) {
  animation-delay: 1s;
}

.tips-list li:nth-child(3) {
  animation-delay: 1.1s;
}

.tips-list li:nth-child(4) {
  animation-delay: 1.2s;
}

/* Action Buttons */
.edit-user-actions,
.change-password-actions {
  background-color: #f8f9fa;
  padding: 16px 24px;
  gap: 12px;
}

.edit-user-actions {
  display: flex;
  align-items: center;
}

/* .dialog-action-btn, */
.cancel-btn,
.save-btn {
  text-transform: none;
  font-weight: 600;
  border-radius: 8px;
  height: 44px;
  min-width: 120px;
  transition: all 0.2s ease;
}

/* .dialog-action-btn {
  padding: 0 20px;
  height: 40px;
  min-width: 140px;
} */

.cancel-btn,
.save-btn {
  padding: 0 24px;
}

.cancel-btn:hover {
  background-color: rgba(var(--v-theme-surface), 0.8);
  transform: translateY(-1px);
}

/* .dialog-action-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(33, 150, 243, 0.3);
} */

.save-btn:hover:not(:disabled) {
  transform: translateY(-1px);
}

.edit-user-actions .save-btn {
  background: linear-gradient(135deg, #2196f3 0%, #1976d2 100%);
}

.edit-user-actions .save-btn:hover {
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
  box-shadow: 0 4px 12px rgba(33, 150, 243, 0.4);
}

.change-password-actions .save-btn {
  background: linear-gradient(135deg, #ff9800 0%, #f57c00 100%);
}

.change-password-actions .save-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #f57c00 0%, #ef6c00 100%);
  box-shadow: 0 4px 12px rgba(255, 152, 0, 0.4);
}

.change-password-actions .save-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none !important;
  box-shadow: none !important;
}

/* Animations */
.mdi-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

@keyframes slideInDown {
  from {
    opacity: 0;
    transform: translateY(-30px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideInRequirement {
  from {
    opacity: 0;
    transform: translateX(-10px);
  }

  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes slideInTip {
  from {
    opacity: 0;
    transform: translateX(-10px);
  }

  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Vue Transitions */
.expand-fade-enter-active {
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

.expand-fade-leave-active {
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.expand-fade-enter-from {
  opacity: 0;
  transform: translateY(-15px) scaleY(0.8);
  max-height: 0;
  padding-top: 0;
  padding-bottom: 0;
  margin-top: 0;
}

.expand-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px) scaleY(0.9);
  max-height: 0;
  padding-top: 0;
  padding-bottom: 0;
  margin-top: 0;
}

.expand-fade-enter-to,
.expand-fade-leave-from {
  opacity: 1;
  transform: translateY(0) scaleY(1);
  max-height: 200px;
  padding-top: 8px;
  padding-bottom: 8px;
  margin-top: 8px;
}

/* Slide Fade Transitions */
.slide-fade-enter-active {
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-fade-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-fade-enter-from {
  opacity: 0;
  transform: translateY(-10px) scale(0.95);
  filter: blur(2px);
}

.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-5px) scale(0.98);
  filter: blur(1px);
}

.slide-fade-enter-to,
.slide-fade-leave-from {
  opacity: 1;
  transform: translateY(0) scale(1);
  filter: blur(0);
}

/* Flip Icon */
.flip-right {
  transform: scaleX(-1);
}

/* Password Toggle Button */
.password-toggle {
  margin: 0 !important;
}

.password-toggle-icon {
  width: 20px;
  height: 20px;
  object-fit: contain;
}

/* Security Tips Style */
.security-tips-style {
  margin-bottom: 15px;
  animation: fadeInUp 0.5s ease-out 0.3s both;
}

.template-description {
  color: #171717;
  display: flex;
  padding: 15px 20px;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  border-radius: 16px;
  border: 1px solid #C9C9C9;
  background: linear-gradient(90deg, #FFF2F2 -15.8%, #FFFFFF 100%);
}

/* Dark Theme Support */
.v-theme--dark .main-content-card {
  background: #1f1f1f !important;
  border-color: rgba(255, 255, 255, 0.08) !important;
}

.v-theme--dark .search-panel-section {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.12);
}

.v-theme--dark .custom-search-input-container {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.16);
  box-shadow: none;
}

.v-theme--dark .custom-search-input-container:focus-within {
  border-color: #f67760;
  box-shadow: 0 4px 16px rgba(246, 119, 96, 0.24);
}

.v-theme--dark .custom-search-input {
  color: rgba(255, 255, 255, 0.87);
}

.v-theme--dark .custom-search-input-icon,
.v-theme--dark .clear-icon {
  color: rgba(255, 255, 255, 0.65);
}

.v-theme--dark .action-btn-sync {
  background: rgba(56, 89, 208, 0.2) !important;
  border-color: rgba(56, 89, 208, 0.45) !important;
  color: #d5dcff !important;
  box-shadow: none !important;
}

.v-theme--dark .action-btn-sync:hover:not(:disabled) {
  background: rgba(56, 89, 208, 0.3) !important;
}

.v-theme--dark .action-btn-setup {
  box-shadow: 0 4px 18px rgba(76, 175, 80, 0.45) !important;
}

.v-theme--dark .user-group-data-table :deep(thead) {
  background-color: rgba(195, 46, 14, 0.32) !important;
  color: #ffffff !important;
}

.v-theme--dark .table-footer-wrapper {
  color: rgba(255, 255, 255, 0.7);
}

.v-theme--dark .no-data-title {
  color: #e0e0e0;
}

.v-theme--dark .no-data-subtitle {
  color: #bdbdbd;
}

.v-theme--dark .action-divider {
  background: rgba(255, 255, 255, 0.2);
}

/* Responsive Design */
@media (max-width: 768px) {
  .page-header-content {
    flex-direction: column;
    align-items: flex-start;
  }

  .header-actions-group {
    width: 100%;
    justify-content: center;
  }

  .action-btn-sync,
  .action-btn-setup {
    width: 100%;
    min-width: 160px;
  }

  .table-footer-wrapper {
    flex-direction: column;
    align-items: flex-start !important;
    gap: 12px;
  }

  .search-panel-section {
    padding: 12px 16px;
  }

  .no-data-container {
    min-height: 250px;
    padding: 30px 16px;
  }

  .no-data-content {
    gap: 20px;
  }

  .no-data-icon {
    font-size: 56px !important;
  }

  .no-data-title {
    font-size: 1.1rem;
  }

  .no-data-subtitle {
    font-size: 0.85rem;
  }

  .no-data-btn {
    width: 100%;
    max-width: 200px;
  }

  .edit-user-dialog,
  .change-password-dialog {
    margin: 16px;
    max-width: calc(100vw - 32px) !important;
  }

  .edit-user-header,
  .change-password-header {
    padding: 16px 20px;
  }

  .edit-user-content,
  .change-password-content {
    padding: 16px;
    max-height: 60vh;
  }

  .edit-user-actions {
    flex-direction: column;
    gap: 8px;
  }

  .cancel-btn {
    width: 100%;
    order: 2;
  }

  .save-btn {
    width: 100%;
    order: 3;
  }
}

@media (max-width: 480px) {
  .header-actions-group {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }

  .action-btn-sync,
  .action-btn-setup {
    width: 100%;
  }

  .search-panel-section {
    padding: 12px;
  }

  .custom-search-input-container {
    padding: 10px 12px;
  }

  .table-footer-wrapper {
    align-items: stretch !important;
  }

  .action-group {
    gap: 8px;
  }

  .edit-user-dialog,
  .change-password-dialog {
    margin: 8px;
    max-width: calc(100vw - 16px) !important;
  }

  .change-password-actions {
    flex-direction: column;
    gap: 8px;
  }

  .cancel-btn,
  .save-btn {
    width: 100%;
  }
}
.apply-filter-apply-btn {
  background: linear-gradient(90deg, #C32E0E 0%, #F67760 100%) !important;
  color: white !important;
  border-radius: 10px !important;
  text-transform: none;
  font-weight: 500;
  min-width: 100px;
  height: 37px !important;
}

.apply-filter-apply-btn:hover {
  opacity: 0.9;
}
.apply-filter-cancel-btn {
  border-radius: 10px !important;
  text-transform: none;
  font-weight: 500;
  min-width: 100px;
  height: 37px !important;
  border-color: #ccc !important;
  color: #333 !important;
}
.apply-filter-cancel-btn:hover {
  background-color: #f5f5f5 !important;
}

/* Redesign  */
.border-section-wrapper{
  border-radius: 24px;
  padding: 15px 20px;
  border: 1px solid #CCCCCC;
  transition: all 0.3s ease;
}
.flip-right {
  transform: scaleX(-1);
}
.basic-info-section{
  border-bottom: 1px solid #CCCCCC;
}
.basic-info-container {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  margin-bottom: 16px;
  padding-bottom: 8px;
}

.basic-info-icon {
  width: 24px;
  height: 24px;
  max-width: 24px;
  max-height: 24px;
  flex-shrink: 0;
  object-fit: contain;
}

.basic-info-text {
  font-size: 1.1rem;
  font-weight: 600;
  color: rgb(var(--v-theme-on-surface));
}

/* Model Autocomplete Styles */
.model-autocomplete :deep(.v-autocomplete__selection) {
  max-width: 100%;
  overflow: visible;
}

.model-autocomplete :deep(.v-autocomplete__selection:empty) {
  display: none !important;
}

.model-autocomplete :deep(.v-chip .v-chip__content) {
  color: #C30000 !important;
}

.model-autocomplete :deep(.v-chip .v-chip__close) {
  display: flex;
  align-items: center;
  padding: 0px 8px 0px 4px;
  border-left: 1px solid #646464;
  color: #646464 !important;
  opacity: 0.6;
  font-size: 14px !important;
  width: auto !important;
  height: 100%;
}

.model-autocomplete :deep(.v-chip .v-chip__close:hover) {
  opacity: 1;
}

.model-chip {
  border: 1px solid #C30000 !important;
  color: #C30000 !important;
  background-color: #FFFFFF !important;
  border-radius: 999px !important;
  font-weight: 600;
  letter-spacing: 0.02em;
  margin: 2px 4px 2px 0;
  padding: 0 8px !important;
}

.more-chip {
  color: #C30000 !important;
  border: 1px solid #C30000 !important;
  background-color: rgba(195, 0, 0, 0.08) !important;
  font-weight: 600;
}

.model-option-item {
  border-radius: 10px;
  transition: background 0.2s ease, border 0.2s ease;
}

:deep(.model-option-item--selected) {
  background: rgba(195, 0, 0, 0.08);
  border: 1px solid rgba(195, 0, 0, 0.25);
}

:deep(.model-option-checkbox) {
  margin-right: 8px;
}

:deep(.model-option-checkbox .v-selection-control__input) {
  width: 20px;
  height: 20px;
}

:deep(.model-option-title) {
  font-weight: 500;
}

:deep(.model-info-icon) {
  color: rgba(var(--v-theme-on-surface), 0.6);
  cursor: pointer;
  transition: color 0.2s ease;
}

:deep(.model-info-icon:hover) {
  color: #C30000;
}

.model-all-selected-label {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.875rem;
  font-weight: 600;
  color: #C30000;
  padding: 6px 12px;
  border-radius: 16px;
  background-color: rgba(195, 0, 0, 0.08);
  white-space: nowrap;
  flex-shrink: 0;
}

.model-all-divider {
  width: 100% !important;
  border-color: #CCCCCC !important;
  height: 2px !important;
  opacity: 1 !important;
}

:deep(.model-all-option) {
  display: flex !important;
  align-items: center !important;
  gap: 8px !important;
  padding: 10px 14px !important;
  border-radius: 12px !important;
  cursor: pointer !important;
  transition: background 0.2s ease, border 0.2s ease, box-shadow 0.2s ease !important;
  width: 100% !important;
}

:deep(.model-all-option .v-list-item__content) {
  display: flex !important;
  align-items: center !important;
}

:deep(.model-all-title) {
  display: inline-flex !important;
  align-items: center !important;
  gap: 6px !important;
}

:deep(.model-all-option--active) {
  background: rgba(195, 0, 0, 0.08);
  border: 1px solid rgba(195, 0, 0, 0.3);
  box-shadow: 0 2px 8px rgba(195, 0, 0, 0.12);
}

:deep(.model-all-option--disabled) {
  opacity: 0.5;
  cursor: not-allowed;
}

:deep(.model-all-option__label) {
  white-space: nowrap !important;
  font-weight: 600;
  color: rgb(var(--v-theme-on-surface));
}
.image-icon-style {
  width: 24px;
  height: 24px;
  margin-right: 8px;
  flex-shrink: 0;
  object-fit: contain;
}
</style>
